import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContentFormats } from "../hooks/useContentFormats";
import { useSortedContentFormats } from "../hooks/useSortedContentFormats";
import { ContentFormatType } from "../types";
import { ROUTES } from "../constants/routes";

const DURATION = 1600;

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const CountUp = ({ target, start }: { target: number; start: boolean }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return;

    let raf: number;
    const startTime = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / DURATION, 1);
      setValue(Math.round(easeOutCubic(progress) * target));

      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [start, target]);

  return <>{value}</>;
};

const StatItem = ({
  format,
  start,
}: {
  format: ContentFormatType;
  start: boolean;
}) => {
  const navigate = useNavigate();

  const openFormat = () => {
    navigate(`${ROUTES.content}?contentFormatIds=${format.id}`);
  };

  return (
    <button
      type="button"
      className="landing-stats_item"
      onClick={openFormat}
      aria-label={`Pogledaj ${format.name}`}
    >
      <span className="landing-stats_name">{format.name}</span>
      <div className="landing-stats_metrics">
        <div className="landing-stats_metric">
          <span className="landing-stats_number">
            <CountUp target={format.serialCount} start={start} />
          </span>
          <span className="landing-stats_label">Serijala</span>
        </div>
        <span className="landing-stats_divider" aria-hidden />
        <div className="landing-stats_metric">
          <span className="landing-stats_number">
            <CountUp target={format.episodeCount} start={start} />
          </span>
          <span className="landing-stats_label">Epizoda</span>
        </div>
      </div>
    </button>
  );
};

const LandingStats = () => {
  const { data: formats } = useContentFormats();
  const { data: orderedFormats } = useSortedContentFormats();
  const [hasStarted, setHasStarted] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const data = useMemo(() => {
    if (!formats?.length) return [];

    if (!orderedFormats?.length) return formats;

    const orderMap = new Map(
      orderedFormats.map((format, index) => [format.id, index])
    );

    return [...formats].sort((a, b) => {
      const orderA = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }, [formats, orderedFormats]);

  const setRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();

    if (!node) return;

    observerRef.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHasStarted(true);
        observerRef.current?.disconnect();
      }
    });

    observerRef.current.observe(node);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  if (data.length === 0) {
    return null;
  }

  return (
    <div className="landing-stats" ref={setRef}>
      <div className="landing-stats_head">
        <h2 className="section-title landing-stats_title">NeoUm u brojkama</h2>
      </div>
      <div className="landing-stats_row">
        {data.map((format) => (
          <StatItem key={format.id} format={format} start={hasStarted} />
        ))}
      </div>
    </div>
  );
};

export default LandingStats;
